import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cn from 'classnames';
import {
  ButtonGroup,
  Modal,
  ButtonToolbar,
  Dropdown,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import { List } from 'immutable';
import { injectIntl } from 'react-intl';
import Button from '../ui/Button';
import appConfig from '../../appConfig';
import { updateSortBy } from '../../actions/umoGallery';
import { purgeRecyclebin } from '../../actions/categories';
import {
  showUMOGridView,
  showUMOGridNoJView,
  showUMOListView,
  showUMOSmGridNoJView,
  showUMOCarouselView,
  showUMOModifyDialog,
} from '../../actions/mainUI';
import { viewTypes } from '../../constants/umoGallery';
import { SearchParams } from '../../utils/searchUtils';
import { getLabelFromAppConfig, showUploadTemplate } from '../../utils/uiUtils';
import { validateSessionConnection } from '../../actions/session';
import { addError } from '../../actions/messageDialog';
import OptionsMenu from '../OptionsMenu';
import { UMO_MODIFY_TYPE_ZIP_DOWNLOAD } from '../../constants/umoModifications';
import FilterIcon from '../../../icons/sub-menu-and-common-icons/filter.svg';
import DownloadIcon from '../../../icons/sub-menu-and-common-icons/dowload.svg';
import GridView1 from '../../../icons/sub-menu-and-common-icons/tile-view1.svg';
import GridView2 from '../../../icons/sub-menu-and-common-icons/tile-view2.svg';
import GridView3 from '../../../icons/sub-menu-and-common-icons/tile-view3.svg';
import ListViewIcon from '../../../icons/sub-menu-and-common-icons/list-view.svg';
import PicViewIcon from '../../../icons/sub-menu-and-common-icons/pic-view.svg';
import languageIcon from '../../../icons/toolbar-icons/language.jpg';
import UploadBlack from '../Icons/UploadBlack';
import { isValidForFileUploader } from '../FilesUploader/utils';

const mapStateToProps = (state) => ({
  session: state.session,
  categories: state.categories,
  isLoading: state.umoGallery.get('isRequesting'),
  selectedCategory: state.umoGallery.get('selectedCategory'),
  mainUI: state.mainUI,
});

const mapDispatchToProps = (dispatch) => ({
  sortByUpdate: (eventKey, isDesc) => dispatch(updateSortBy(eventKey, isDesc)),
  validateSession: (surl) => dispatch(validateSessionConnection(surl)),
  addError: (err) => dispatch(addError(err)),
  dispatchShowFileUploader: (categoryID, categoryName) =>
    showUploadTemplate(dispatch, categoryID, categoryName),
  dispatchErrorMessage: (message) => dispatch(addError(message)),
  emptyRecyclebin: (categoryID) => dispatch(purgeRecyclebin(categoryID)),
});

function UMOToolbar({
  isFilterExpanded,
  onFilterToggle,
  sortBy,
  sortByIsDescending,
  dispatch,
  filterByList,
  sortByUpdate,
  umoGalleryViewType,
  searchParams,
  selectedWorkspaceData = { id: null },
  selectedUMOIDs,
  isSelectAll,
  isUMOGalleryWorkspace,
  copyUMOs,
  intl,
  umoList,
  session,
  toggleCompareView,
  isLoading,
  languageList,
  changeDataLanguage,
  selectedLanguage,
  dispatchShowFileUploader,
  dispatchErrorMessage,
  mainUI,
  selectedCategory,
  umoTotal,
  emptyRecyclebin,
  isMetaAttachView,
}) {
  // created as a general function to be able to clear modes when switching between views
  // in this case, sort mode
  const [sortPanelVisible, setSortPanelVisible] = useState(false);
  const [selectedSortValue, setSelectedSort] = useState(sortBy);
  const [selectedSortIsDesc, setSelectedSortIsDesc] = useState(sortByIsDescending);
  const [isValidForUploading, setIsValidForUploading] = useState(false);
  const isDownloadDisabled = isSelectAll ? umoTotal > 100 : selectedUMOIDs.size === 0;
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);

  // Effect to check if file uploading is valid and set related states
  useEffect(() => {
    const isValid = isValidForFileUploader(searchParams, selectedCategory);
    setIsValidForUploading(isValid);
  }, [searchParams, selectedCategory]);

  const changeViewType = (viewType) => {
    switch (viewType) {
      case viewTypes.LIST:
        dispatch(showUMOListView());
        break;
      case viewTypes.GRID_NOJ:
        dispatch(showUMOGridNoJView());
        break;
      case viewTypes.GRID:
        dispatch(showUMOGridView());
        break;
      case viewTypes.SM_GRID_NOJ:
        dispatch(showUMOSmGridNoJView());
        break;
      case viewTypes.CAROUSEL:
        dispatch(showUMOCarouselView());
        break;
      default:
        break;
    }
  };
  const onDownloadClick = () => {
    dispatch(
      showUMOModifyDialog(
        { modType: UMO_MODIFY_TYPE_ZIP_DOWNLOAD },
        selectedUMOIDs.size > 0 ? selectedUMOIDs.toJS() : null,
        searchParams,
        { id: selectedWorkspaceData.id }
      )
    );
  };

  const onUploadClick = () => {
    if (mainUI.getIn(['fileUploader', 'isUploading']))
      dispatchErrorMessage(
        intl.formatMessage({ id: 'fileUploader.uploadInProgressMessage' })
      );
    else
      dispatchShowFileUploader(
        selectedCategory.get('i'),
        selectedCategory.get('n')
      );
  };

  const onPurgeClick = () => {
    setShowPurgeConfirm(true);
  };

  const handlePurgeConfirm = () => {
    emptyRecyclebin(selectedCategory.get('i'));
    setShowPurgeConfirm(false);
  };

  let filterByCount = 0;

  if (filterByList) {
    filterByList.forEach((filterByItem) => {
      if (filterByItem.get('value')) {
        filterByCount += 1;
      }
    });
  }

  const sortModalOptions = appConfig.umoGallery.sortByOptions.map(
    (option, i) => (
      <li
        className="align-items-center d-flex justify-content-between"
        key={`${option}_${i}`}
      >
        <label>
          <input
            className="me-1 mb-1"
            type="radio"
            checked={option.value === selectedSortValue}
            onChange={() => {
              setSelectedSort(option.value);
              setSelectedSortIsDesc(false);
            }}
          />
          {getLabelFromAppConfig(option, intl.locale)}
        </label>
        <ButtonToolbar>
          <button
            className={cn(
              'me-1 mb-1 btn btn-sm umo-toolbar__sort-item',
              option.value === selectedSortValue && !selectedSortIsDesc
                ? 'btn-primary umo-toolbar__sort-item--selected text-light'
                : 'btn-outline-primary'
            )}
            onClick={() => {
              setSelectedSort(option.value);
              setSelectedSortIsDesc(false);
            }}
          >
            <i className="umo-toolbar__sort-item--up" />
          </button>
          <button
            className={cn(
              'me-1 mb-1 btn btn-sm umo-toolbar__sort-item',
              option.value === selectedSortValue && selectedSortIsDesc
                ? 'btn-primary umo-toolbar__sort-item--selected text-light'
                : 'btn-outline-primary'
            )}
            onClick={() => {
              setSelectedSort(option.value);
              setSelectedSortIsDesc(true);
            }}
          >
            <i className="umo-toolbar__sort-item--down" />
          </button>
        </ButtonToolbar>
      </li>
    )
  );
  const sortModal = (
    <Modal
      show={sortPanelVisible}
      onHide={() => {
        setSortPanelVisible(false);
        setSelectedSortIsDesc(sortByIsDescending);
        setSelectedSort(sortBy);
      }}
    >
      <Modal.Header closeButton className="umo-toolbar__sort-modal">
        <Modal.Title className="umo-gallery-header__lightbox-name">
          {intl.formatMessage({ id: 'searchResults.sortBy' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="umo-toolbar__sort-modal-body">
        <ul>{sortModalOptions}</ul>
      </Modal.Body>
      <Modal.Footer className="umo-toolbar__sort-modal-footer">
        <Button
          variant="primary"
          onClick={() => {
            sortByUpdate(selectedSortValue, selectedSortIsDesc);
            setSortPanelVisible(false);
          }}
        >
          {intl.formatMessage({ id: 'umoModifyDialog.buttonApply' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const purgeConfirmationModal = (
    <Modal
      show={showPurgeConfirm}
      onHide={() => setShowPurgeConfirm(false)}
      centered
      className="umo-toolbar__purge-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({ id: 'searchResults.purgeConfirmationTitle' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {intl.formatMessage({ id: 'searchResults.purgeConfirmMessage' })}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={() => setShowPurgeConfirm(false)}>
          {intl.formatMessage({ id: 'general.cancel' })}
        </Button>
        <Button variant="primary" onClick={handlePurgeConfirm}>
          {intl.formatMessage({ id: 'searchResults.purge' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className="umo-gallery-header__umo-toolbar">
      <div className="umo-toolbar__sort-by">
        <ButtonGroup size="sm">
          <Button
            active={umoGalleryViewType === viewTypes.GRID}
            onClick={() => changeViewType(viewTypes.GRID)}
          >
            <img src={GridView1} className="umo-toolbar__icon" />
          </Button>
          <Button
            active={umoGalleryViewType === viewTypes.GRID_NOJ}
            onClick={() => changeViewType(viewTypes.GRID_NOJ)}
          >
            <img src={GridView2} className="umo-toolbar__icon" />
          </Button>
          <Button
            active={umoGalleryViewType === viewTypes.SM_GRID_NOJ}
            onClick={() => changeViewType(viewTypes.SM_GRID_NOJ)}
          >
            <div>
              <img src={GridView3} className="umo-toolbar__icon" />
            </div>
          </Button>
          <Button
            active={umoGalleryViewType === viewTypes.LIST}
            onClick={() => changeViewType(viewTypes.LIST)}
          >
            <img src={ListViewIcon} className="umo-toolbar__icon" />
          </Button>
          <Button
            active={umoGalleryViewType === viewTypes.CAROUSEL}
            onClick={() => changeViewType(viewTypes.CAROUSEL)}
          >
            <img src={PicViewIcon} className="umo-toolbar__icon" />
          </Button>
        </ButtonGroup>
      </div>
      <div className="umo-toolbar__sort">
        <Button
          size="sm"
          disabled={!umoList || umoList.size === 0}
          onClick={() => setSortPanelVisible(true)}
        >
          {intl.formatMessage({ id: 'searchResults.sortBy' })}
        </Button>
      </div>
      {session.get('acctType') === 'G' ? null : (
        <div className="umo-toolbar__options">
          <OptionsMenu
            dispatch={dispatch}
            searchParams={searchParams}
            selectedWorkspaceData={selectedWorkspaceData}
            selectedUMOIDs={selectedUMOIDs}
            isSelectAll={isSelectAll}
            isUMOGalleryWorkspace={isUMOGalleryWorkspace}
            className="font-xs-regular"
            title={intl.formatMessage({ id: 'searchResults.options' })}
            size="sm"
            id="umo-options-dropdown"
            umoList={umoList}
            disabled={!umoList || umoList.size === 0}
          />
        </div>
      )}
      <OverlayTrigger
        overlay={
          <Tooltip id="tooltip-disabled">
            {intl.formatMessage(
              { id: 'searchResults.download.tooManyItems' },
              { maxItems: appConfig.advanced.maxDirectDownloadItems || 200 }
            )}
          </Tooltip>
        }
        placement="bottom"
        trigger={isSelectAll && umoTotal > 100 ? ['hover', 'focus'] : []}
      >
        <div className="umo-toolbar__download">
          <Button
            size="sm"
            onClick={onDownloadClick}
            disabled={isDownloadDisabled}
          >
            <div>
              <img src={DownloadIcon} className="umo-toolbar__download-icon" />
            </div>
          </Button>
        </div>
      </OverlayTrigger>
      {appConfig.umoGallery.showCategoryUploadButton && isValidForUploading && (
        <div className="umo-toolbar__download">
          <Button size="sm" onClick={onUploadClick} disabled={isLoading}>
            <div>
              <UploadBlack />
            </div>
          </Button>
        </div>
      )}
      {appConfig.enableMultiLang && languageList && languageList.length > 1 ? (
        <Dropdown
          className="umo-toolbar__change-language-dropdown"
          onSelect={(langIndex) => {
            changeDataLanguage(languageList[langIndex]);
          }}
        >
          <Dropdown.Toggle
            variant="default"
            size="sm"
            id="dropdown-basic"
            className="umo-toolbar__change-language-button"
          >
            <img
              src={languageIcon}
              alt="LanguageChange"
              className="umo-toolbar__language-icon"
            />
            {selectedLanguage && selectedLanguage.$
              ? selectedLanguage.$.display
              : 'English'}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {languageList.map((lang, i) => (
              <Dropdown.Item key={lang._} value={lang._} eventKey={i}>
                {lang.$.display}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      ) : null}
      {appConfig.enableCopyUMOs &&
      !isUMOGalleryWorkspace &&
      (isSelectAll || selectedUMOIDs.size > 0) ? (
        <div className="umo-toolbar__options">
          <Button variant="outline-info" size="sm" draggable onClick={copyUMOs}>
            <div className="umo-toolbar__button umo-toolbar__outline-button  font-xs-regular">
              <i className="umo-toolbar__copy-icon" />
              &nbsp;
              {intl.formatMessage({ id: 'searchResults.copy' })}
            </div>
          </Button>
        </div>
      ) : null}
      {appConfig.umoGallery.filterByOptions && (
        <div className="umo-toolbar__filter">
          <Button
            onClick={() => onFilterToggle(!isFilterExpanded)}
            variant="default"
            size="sm"
            className={isFilterExpanded ? 'umo-toolbar__filter-active' : ''}
            disabled={isLoading}
          >
            <div className="umo-toolbar__button">
              <img src={FilterIcon} className="umo-toolbar__filter-icon" />
              &nbsp;
              <div className="font-xs-regular">
                {intl.formatMessage({ id: 'searchResults.filter' })}
              </div>
              &nbsp;
              {filterByCount > 0 && (
                <label variant="primary">{filterByCount}</label>
              )}
            </div>
          </Button>
        </div>
      )}
      {!isMetaAttachView && appConfig.enableUmoCompare &&
      selectedUMOIDs.size > 1 &&
      selectedUMOIDs.size < 4 ? (
        <div className="umo-toolbar__options">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={toggleCompareView}
          >
            <div>
              <i className="fas fa-clone" />
              &nbsp;
              {intl.formatMessage({ id: 'searchResults.compare' })}
            </div>
          </Button>
        </div>
      ) : null}
      {appConfig.advanced.allowRecycleBinPurge && selectedCategory && selectedCategory.get('rbin') === 'T' && (
        <div className="umo-toolbar__purge">
          <Button
            size="sm"
            variant="danger"
            onClick={onPurgeClick}
            disabled={isLoading}
          >
            <div className="umo-toolbar__button">
              <i className="umo-toolbar__purge-icon" />
              &nbsp;
              {intl.formatMessage({ id: 'searchResults.purge' })}
            </div>
          </Button>
        </div>
      )}
      {purgeConfirmationModal}
      {sortModal}
    </div>
  );
}

UMOToolbar.propTypes = {
  dispatch: PropTypes.func,
  sortByUpdate: PropTypes.func,
  sortBy: PropTypes.string,
  sortByIsDescending: PropTypes.bool,
  filterByList: PropTypes.instanceOf(Array),
  umoGalleryViewType: PropTypes.string,
  searchParams: PropTypes.instanceOf(SearchParams),
  selectedWorkspaceData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  selectedUMOIDs: PropTypes.instanceOf(List),
  isSelectAll: PropTypes.bool,
  isUMOGalleryWorkspace: PropTypes.bool,
  copyUMOs: PropTypes.func,
  umoList: PropTypes.instanceOf(List),
  toggleCompareView: PropTypes.func,
  changeDataLanguage: PropTypes.func,
  selectedLanguage: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  languageList: PropTypes.instanceOf(Array),
  umoTotal: PropTypes.number,
  emptyRecyclebin: PropTypes.func.isRequired,
  isMetaAttachView: PropTypes.bool,
};

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(UMOToolbar)
);
